import React, { useEffect, useRef, useState } from 'react';
import { flowComponentRegistry } from '../../flow/componentRegistry';
import FlowRenderer from '../../flow/FlowRenderer';
import { CanonicalPreview } from '../../flow/preview/CanonicalPreviewRenderer';
import { FlowRuntimeProvider } from '../../flow/FlowRuntimeContext';
import type { FlowConfig, FlowValues } from '../../flow/types';
import {
  boundedPreviewJSON,
  PREVIEW_CHANNEL,
  PREVIEW_PROTOCOL,
  CANONICAL_PREVIEW_PROTOCOL,
  exactPreviewOrigin,
  message,
  previewMessage,
  type PreviewMessage,
} from '../../flow/preview/protocol';

function connection() {
  const params = new URLSearchParams(window.location.search);
  const session = params.get('previewSession') ?? '';
  const requested = params.get('parentOrigin') ?? '';
  const origin = exactPreviewOrigin(requested);
  const configured = String(
    import.meta.env.VITE_CX_STUDIO_PREVIEW_ORIGINS ?? 'http://localhost:8081'
  );
  const allowed = configured
    .split(',')
    .map((value) => value.trim())
    .filter((value) => exactPreviewOrigin(value) === value);
  return origin &&
    origin === requested &&
    allowed.includes(origin) &&
    /^[a-zA-Z0-9-]{16,80}$/.test(session)
    ? { origin, session, instance: crypto.randomUUID() }
    : null;
}

class PreviewBoundary extends React.Component<
  React.PropsWithChildren<{ onError: () => void }>,
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {
    this.props.onError();
  }
  render() {
    return this.state.failed ? (
      <p role="alert">This preview configuration could not be rendered.</p>
    ) : (
      this.props.children
    );
  }
}
function Committed({ onCommit }: { onCommit: () => void }) {
  useEffect(onCommit, [onCommit]);
  return null;
}

export default function ConfigurableFlowPreview() {
  const [scope] = useState(connection);
  const [current, setCurrent] = useState<PreviewMessage | null>(null);
  const [error, setError] = useState('');
  const wireVersion = useRef<PreviewMessage['version']>(PREVIEW_PROTOCOL);
  const active = useRef(0);
  const acknowledged = useRef(0);
  const pendingCompletion = useRef<{ revision: number; payload: Record<string, unknown> } | null>(null);
  const completionSequence = useRef(0);
  const alive = useRef(false);
  const post = React.useCallback(
    (type: PreviewMessage['type'], revision: number, payload?: Record<string, unknown>) => {
      if (!scope || !alive.current || window.parent === window) return;
      const envelope = message(
        type,
        scope.session,
        revision,
        payload,
        scope.instance,
        wireVersion.current
      );
      // Optional undefined fields in local callbacks do not cross the wire.
      if (!boundedPreviewJSON(envelope, true)) return;
      const wire = JSON.parse(JSON.stringify(envelope));
      if (previewMessage(wire)) window.parent.postMessage(wire, scope.origin);
    },
    [scope]
  );
  useEffect(() => {
    alive.current = true;
    if (!scope || window.parent === window)
      return () => {
        alive.current = false;
      };
    const receive = (event: MessageEvent) => {
      if (event.source !== window.parent || event.origin !== scope.origin) return;
      const raw = event.data;
      if (
        boundedPreviewJSON(raw) &&
        raw &&
        raw.channel === PREVIEW_CHANNEL &&
        raw.session === scope.session &&
        raw.instance === scope.instance &&
        raw.type === 'configure' &&
        Number.isSafeInteger(raw.revision) &&
        raw.revision > active.current &&
        ![PREVIEW_PROTOCOL, CANONICAL_PREVIEW_PROTOCOL].includes(raw.version)
      ) {
        acknowledged.current = 0;
        setError('This Studio version is incompatible. Reconnect after updating.');
        post('error', raw.revision, { reason: 'incompatible_version' });
        return;
      }
      const incoming = previewMessage(event.data);
      if (incoming?.type === 'probe' && incoming.session === scope.session) {
        if (!active.current) wireVersion.current = incoming.version;
        post('ready', 0);
        return;
      }
      if (
        !incoming ||
        incoming.session !== scope.session ||
        incoming.instance !== scope.instance ||
        incoming.type !== 'configure' ||
        incoming.version !== wireVersion.current ||
        incoming.revision < active.current
      )
        return;
      // A load probe re-sends the same draft. Re-ack without clearing entered answers.
      if (incoming.revision === active.current) {
        if (acknowledged.current === incoming.revision) {
          post('ack', incoming.revision);
          if (incoming.version === CANONICAL_PREVIEW_PROTOCOL && pendingCompletion.current?.revision === incoming.revision)
            post('complete', incoming.revision, pendingCompletion.current.payload);
        }
        return;
      }
      pendingCompletion.current = null;
      active.current = incoming.revision;
      acknowledged.current = 0;
      if (
        incoming.version === PREVIEW_PROTOCOL &&
        (incoming.payload!.flow as FlowConfig).screens.some((screen) =>
          screen.components.some(
            (component) =>
              !Object.prototype.hasOwnProperty.call(flowComponentRegistry, component.type)
          )
        )
      ) {
        setError('This preview configuration contains an unsupported component.');
        post('error', incoming.revision, { reason: 'invalid_configuration' });
        return;
      }
      setError('');
      setCurrent(incoming);
    };
    window.addEventListener('message', receive);
    const ready = () => {
      if (!active.current) post('ready', 0);
    };
    ready();
    const timer = window.setInterval(ready, 500);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
      window.removeEventListener('message', receive);
    };
  }, [scope, post]);
  const revision = current?.revision ?? 0;
  const commit = React.useCallback(() => {
    if (active.current !== revision || !revision) return;
    acknowledged.current = revision;
    post('ack', revision);
    const pending = pendingCompletion.current;
    if (pending?.revision === revision) post('complete', revision, pending.payload);
  }, [post, revision]);
  const failed = React.useCallback(() => {
    if (active.current !== revision) return;
    acknowledged.current = 0;
    pendingCompletion.current = null;
    setError('This preview configuration could not be rendered.');
    post('error', revision, { reason: 'invalid_configuration' });
  }, [post, revision]);
  const event = (type: 'action' | 'complete', payload: Record<string, unknown>) => {
    if (active.current !== revision) return;
    if (type === 'complete' && current?.version === CANONICAL_PREVIEW_PROTOCOL) {
      // Retain the last visit for recovery after a late load drops ACK/completion.
      const completion = { ...payload, completionId: ++completionSequence.current };
      pendingCompletion.current = { revision, payload: completion };
      if (acknowledged.current === revision) post(type, revision, completion);
    } else if (acknowledged.current === revision) post(type, revision, payload);
  };
  if (!scope) return <p role="alert">Open this preview from an authorized CX Studio.</p>;
  if (!current)
    return error ? (
      <p role="alert">{error}</p>
    ) : (
      <p role="status">Waiting for the current Studio draft…</p>
    );
  const payload = current.payload!;
  const authoring =
    current.version === CANONICAL_PREVIEW_PROTOCOL &&
    payload.authoring &&
    typeof payload.authoring === 'object'
      ? (payload.authoring as { mode?: string; selectedComponentId?: string })
      : undefined;
  const authoringSelectedComponentId =
    authoring?.mode === 'select' ? authoring.selectedComponentId : undefined;
  const flow = payload.flow as FlowConfig | undefined;
  // Preview owns effect policy independently of authorable component configuration.
  const previewFlow = {
    ...flow,
    screens: (flow?.screens ?? []).map((screen) => ({
      ...screen,
      components: screen.components.map((component) =>
        component.type === 'tipping' ? { ...component, checkoutEnabled: false } : component
      ),
    })),
  };
  const blockNavigation = (e: React.SyntheticEvent) => {
    if (e.target instanceof Element && e.target.closest('a[href],area[href],form')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const selectAuthoringComponent = (e: React.SyntheticEvent) => {
    if (authoring?.mode !== 'select' || !(e.target instanceof Element)) return false;
    const component = e.target.closest<HTMLElement>('[data-component-id]');
    const componentId = component?.dataset.componentId;
    if (!componentId) return false;
    e.preventDefault();
    e.stopPropagation();
    event('action', {
      action: { type: 'preview_component_select', componentId },
    });
    return true;
  };
  const authoringStyles = authoringSelectedComponentId
    ? `[data-configurable-preview-scroll][data-preview-authoring="select"] [data-component-id="${authoringSelectedComponentId}"] { outline: 2px solid #2563eb; outline-offset: 2px; }`
    : '';
  return (
    <div
      data-configurable-preview-scroll
      data-preview-authoring={authoring?.mode === 'select' ? 'select' : undefined}
      data-preview-revision={revision}
      onClickCapture={(e) => {
        if (!selectAuthoringComponent(e)) blockNavigation(e);
      }}
      onAuxClickCapture={blockNavigation}
      onContextMenuCapture={blockNavigation}
      onKeyDownCapture={(e) => {
        if (['Enter', ' '].includes(e.key)) selectAuthoringComponent(e);
      }}
      onSubmitCapture={(e) => {
        if (!selectAuthoringComponent(e)) blockNavigation(e);
      }}
      style={{
        width: '100%',
        height: '100dvh',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {authoring?.mode === 'select' ? (
        <style>{`
          [data-configurable-preview-scroll][data-preview-authoring="select"] [data-component-id] { cursor: pointer; }
          [data-configurable-preview-scroll][data-preview-authoring="select"] [data-component-id]:hover { outline: 1px solid rgba(37, 99, 235, 0.55); outline-offset: 2px; }
          ${authoringStyles}
        `}</style>
      ) : null}
      {error ? (
        <p role="alert">{error}</p>
      ) : (
        <PreviewBoundary key={revision} onError={failed}>
          <FlowRuntimeProvider mode="preview">
            {current.version === CANONICAL_PREVIEW_PROTOCOL ? (
              <CanonicalPreview
                payload={payload}
                onAction={(action) => event('action', { action })}
                onComplete={(values) => event('complete', { values })}
                onDestination={(destination) =>
                  event('action', { action: { type: 'preview_destination', destination } })
                }
              />
            ) : (
              <FlowRenderer
                flow={previewFlow as FlowConfig}
                initialScreenId={payload.initialScreenId as string | undefined}
                initialValues={payload.initialValues as FlowValues | undefined}
                onAction={(action) => event('action', { action })}
                onComplete={(values) => event('complete', { values })}
              />
            )}
            <Committed onCommit={commit} />
          </FlowRuntimeProvider>
        </PreviewBoundary>
      )}
    </div>
  );
}
